import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TituloPagina } from 'esocial.web.react.lib/cjs/components/util/Text';
import { useRequisicaoControlada } from 'esocial.web.react.lib/cjs/hooks/RequisicaoControlada';
import { CustomTable } from 'esocial.web.react.lib/cjs/components/util/Table';
import { Button, Chip, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, Grid } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import { isUndefinedOrNull, verificarTodosComponentesTocados } from 'esocial.web.react.lib/cjs/utils/geral';
import { TipoMensagem } from 'esocial.web.react.lib/cjs/utils/enums';
import { Fieldset } from 'esocial.web.react.lib/cjs/components/util/Fieldset';
import { CustomTextField } from 'esocial.web.react.lib/cjs/components/form/Custom/CustomTextField';
import RegraObrigatoriedade from 'esocial.web.react.lib/cjs/components/form/Validation/RegraObrigatoriedade';
import RegraExpressaoRegular from 'esocial.web.react.lib/cjs/components/form/Validation/RegraExpressaoRegular';
import ListaRegraValidacao from 'esocial.web.react.lib/cjs/components/form/Validation/ListaRegraValidacao';
import { styled } from '@mui/material/styles';
import { useAppDispatch } from 'esocial.web.react.lib/cjs/hooks/Reduz';
import { substituirMensagens, limparMensagens } from 'esocial.web.react.lib/cjs/store/reducers/mensagens';
import { useBlob } from 'esocial.web.react.lib/cjs/hooks/Blob';

const NenhumRegistroEncontrado = styled(Typography)(() => ({
    marginTop: '5rem'
}));

const BotaoPesquisa = styled(Button)(() => ({
    marginTop: '16px',
    marginBottom: '16px'
}));

const ContainerBotoesTabelaAcao = styled(TableCell)(() => ({
    '& > *': {
        marginLeft: '8px !important',
        marginRight: '8px !important',
    }
}));

const ContainerBotoesPaginaInferior = styled('div')(() => ({
    margin: '48px 0 0',
    display: 'flex',
    flexDirection: 'row'
}));

function EventosPorReciboListagem() {
    const [modelo, setModelo] = useState(null);
    const [numerosReciboFiltro, setNumerosReciboFiltro] = useState('');
    const [regraValidacaoFormulario, setRegraValidacaoFormulario] = useState({});

    const { get } = useRequisicaoControlada();
    const navigate = useNavigate();
    const disponibilizarParaDownload = useBlob();
    const dispatch = useAppDispatch();

    const objetoComponentesTocados = {};
    objetoComponentesTocados['numerosReciboFiltro'] = false;
    const [componenteTocadoFormulario, setComponenteTocadoFormulario] = useState(objetoComponentesTocados);

    async function handlePesquisar(e) {
        e.preventDefault();
        e.stopPropagation();
        dispatch(limparMensagens());

        const valoresValidacaoFormulario = Object.values(regraValidacaoFormulario).filter((x) => typeof x !== 'boolean');

        if ((isUndefinedOrNull(numerosReciboFiltro) || numerosReciboFiltro.length == 0)) {
            dispatch(substituirMensagens([{ tipo: TipoMensagem.Erro, texto: 'Deve ser informado pelo menos um filtro para pesquisa.', resistente: false }]));
        } else if (valoresValidacaoFormulario.length > 0 && !new ListaRegraValidacao(valoresValidacaoFormulario).existeErro()) {
            carregarModelo();
        }
    }

    async function carregarModelo() {
        const processamentoResposta = (conteudoResposta) => {
            setModelo(conteudoResposta);
        };

        await get(`infoempregador/eventosPorRecibo/${numerosReciboFiltro}/${5}`, processamentoResposta, undefined, { suprimirSobreposicaoMensagens: true });
    }

    async function baixarXmlEvento(idEvento) {
        await disponibilizarParaDownload(`gestaoTrabalhadores/baixarXmlEvento/${idEvento}/${null}`, `eSocial_Evento_${idEvento}`, 'xml', true, { suprimirSobreposicaoMensagens: false });
    }

    async function baixarXmlReciboEvento(idEvento) {
        await disponibilizarParaDownload(`gestaoTrabalhadores/baixarReciboEvento/${idEvento}/${null}`, `eSocial_ReciboEvento_${idEvento}`, 'xml', true, { suprimirSobreposicaoMensagens: false });
    }

    function handleChangeNumerosReciboFiltro(e) {
        // Retirar todos os caracteres inválidos para evitar erros de digitação
        const somenteCaracteresPerimtidos = e.target.value.replace(/[^0-9.;]/g, '');

        setNumerosReciboFiltro(somenteCaracteresPerimtidos);
    }

    useEffect(() => {
        const objetoRegrasValidacao = {};
        objetoRegrasValidacao['numerosReciboFiltro'] = new ListaRegraValidacao([new RegraObrigatoriedade(numerosReciboFiltro), new RegraExpressaoRegular(numerosReciboFiltro, '^1\\.[0-9]\\.([0-9]{19})(;1\\.[0-9]\\.([0-9]{19})){0,19};?$')], componenteTocadoFormulario['numerosReciboFiltro']);
        objetoRegrasValidacao.todosComponentesTocados = verificarTodosComponentesTocados(componenteTocadoFormulario);

        setRegraValidacaoFormulario(objetoRegrasValidacao);
    }, [componenteTocadoFormulario]);

    return (
        <Fragment>
            <TituloPagina>Consultar Eventos por Recibo</TituloPagina>

            {Object.values(regraValidacaoFormulario).length > 0 &&
                <Fieldset legend="Filtro de pesquisa" spacing={4}>
                    <CustomTextField
                        xs={12} md={12}
                        label="Números de Recibos"
                        value={numerosReciboFiltro}
                        onChange={(e) => handleChangeNumerosReciboFiltro(e)}
                        multiline
                        rows={4}
                        maxLength={479}
                        placeholder="Informe os números de recibos separados por ';' (ponto e vírgula). Ex: 1.1.0000000000000000001;1.1.0000000000000000002"
                        title="Máximo de 20 itens separados por ';' (ponto e vírgula)"
                        {...regraValidacaoFormulario['numerosReciboFiltro'].obterPropriedadesValidacao()}
                        onBlur={() => setComponenteTocadoFormulario({ ...componenteTocadoFormulario, 'numerosReciboFiltro': true })}
                    />

                    <Grid item xs={12} md={2} style={{ paddingTop: 0 }}>
                        <BotaoPesquisa
                            variant="contained"
                            color="secondary"
                            fullWidth
                            startIcon={<SearchIcon />}
                            onClick={(e) => handlePesquisar(e)}>
                            Pesquisar
                        </BotaoPesquisa>
                    </Grid>
                </Fieldset>
            }

            {
                (!isUndefinedOrNull(modelo)) &&
                <Fragment>
                    {
                        modelo.length > 0 ?
                            <Fieldset legend="Resultado da pesquisa">
                                <CustomTable>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">Data de Recepção</TableCell>
                                            <TableCell align="center">Data de Processamento</TableCell>
                                            <TableCell align="center">Tipo de Evento</TableCell>
                                            <TableCell align="center">Identificador</TableCell>
                                            <TableCell align="center">Processo de Emissão</TableCell>
                                            <TableCell align="center">Situação</TableCell>
                                            <TableCell align="center" style={{ minWidth: '250px' }}>Ação</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {modelo.map((linha) => (
                                            <TableRow key={linha.idEvento}>
                                                <TableCell align="center">{new Date(linha.dataHoraRecepcao).toLocaleDateString('pt-BR') + ' ' + new Date(linha.dataHoraRecepcao).toLocaleTimeString('pt-BR')}</TableCell>
                                                <TableCell align="center">{new Date(linha.dataHoraProcessamento).toLocaleDateString('pt-BR') + ' ' + new Date(linha.dataHoraProcessamento).toLocaleTimeString('pt-BR')}</TableCell>
                                                <TableCell align="center">{linha.descricaoTipo}</TableCell>
                                                <TableCell align="center">{linha.idEvento}</TableCell>
                                                <TableCell align="center">{linha.descricaoProcessoEmissao}</TableCell>
                                                <TableCell align="center">{linha.situacao}</TableCell>
                                                <ContainerBotoesTabelaAcao align="center">
                                                    <Tooltip title="Baixar XML">
                                                        <Chip
                                                            icon={<GetAppIcon />}
                                                            label="Baixar XML"
                                                            variant="outlined"
                                                            color="primary"
                                                            aria-label="baixar xml do evento"
                                                            onClick={() => baixarXmlEvento(linha.idEvento)}
                                                        />
                                                    </Tooltip>
                                                </ContainerBotoesTabelaAcao>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </CustomTable>
                            </Fieldset> :
                            <NenhumRegistroEncontrado>Nenhum resultado encontrado.</NenhumRegistroEncontrado>
                    }
                </Fragment>
            }

            <ContainerBotoesPaginaInferior>
                <Button variant="outlined" color="secondary" size="medium" onClick={() => navigate('/')}>
                    Voltar
                </Button>
            </ContainerBotoesPaginaInferior>
        </Fragment>
    );
}

export default EventosPorReciboListagem;
